import type { IntlShape } from 'react-intl';
import type { OutreachLiveToday, OutreachLivePhase } from '../outreachTypes';
import { clockIn, safeTimeZone } from './liveFormat';
import { campaignPageMessages as messages } from './campaignPageMessages';

/** The pitches of today that wait to go out: the next one, the two after it and the rest of the queue. */
export function waitingToday(today: OutreachLiveToday | null): number {
  if (today === null || today.next === null) {
    return 0;
  }
  return 1 + today.upNext.length + today.queuedMore;
}

/**
 * The title of the today card (design outreach-v2 g1): "3 pitched, 2 go out by 11:40". The time is
 * the send time of the last known waiting pitch, in the viewer's zone.
 */
export function todayTitle(intl: IntlShape, phase: OutreachLivePhase, today: OutreachLiveToday | null): string {
  if (phase === 'paused') {
    return intl.formatMessage(messages.todayTitlePaused);
  }
  if (phase === 'done') {
    return intl.formatMessage(messages.todayTitleDone);
  }
  const sent = today?.sent ?? 0;
  const left = waitingToday(today);
  if (today === null || left === 0) {
    return intl.formatMessage(messages.todayTitleAllOut, { count: sent });
  }
  const last = today.upNext[today.upNext.length - 1] ?? today.next;
  if (today.queuedMore > 0 || last === null) {
    return intl.formatMessage(messages.todayTitleNoTime, { sent, left });
  }
  return intl.formatMessage(messages.todayTitle, { sent, left, time: clockIn(intl, last.sendAt, undefined) });
}

/**
 * "Next: someone in Berlin at 10:20 their time." A pitch that did not go out has no name on the
 * page (names show after the pitch), so the line says only the place and the time.
 */
export function nextLine(intl: IntlShape, today: OutreachLiveToday | null): string | null {
  const next = today?.next ?? null;
  if (next === null) {
    return null;
  }
  const zone = safeTimeZone(next.timeZone);
  if (next.place === null || zone === undefined) {
    return intl.formatMessage(messages.nextYourTime, { time: clockIn(intl, next.sendAt, undefined) });
  }
  return intl.formatMessage(messages.nextTheirTime, {
    place: intl.formatMessage(messages.nextSomeone, { place: next.place }),
    time: clockIn(intl, next.sendAt, zone),
  });
}
